import { SelectChangeEvent } from '@mui/material';
import { Box, Typography, useTheme } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';

import {
  useAuth,
  getAnnotationToolAnnotatorQueuePath,
  useAppDispatch,
  useAppSelector,
  STATUS_BACKGROUND_MAPPING,
  getStatusCode,
  type StatusText,
} from '@verantos-dev/core-annotation-tool';
import { type DropdownListItem, Button } from '@verantos-dev/shared-components';
import { OverflowTooltip } from '@verantos-dev/ui-annotation-tool';

import { useGetBatchesQuery, useGetProjectsQuery } from '../api/annotatorQueueApiSlice';
import { clearBatchId, setBatchId, setProjectId } from '../state/annotatorQueueSlice';
import { selectProjectId } from '../state/selector';
import type { AnnotationTaskResponse } from '../types/responseTypes';

type DropdownChangeHandler = (
  event: SelectChangeEvent<unknown> | (Event & { target: { value: unknown; name: string } }),
  child: ReactNode
) => void;

export interface UseAnnotatorQueueContentResult {
  annotationProjectOptions: DropdownListItem[];
  isAnnotationProjectsLoading: boolean;
  batchOptions: DropdownListItem[];
  isBatchesLoading: boolean;
  handleProjectChange: DropdownChangeHandler;
  handleBatchChange: DropdownChangeHandler;
  columns: GridColDef<AnnotationTaskResponse>[];
}

export const useAnnotatorQueueContent = (): UseAnnotatorQueueContentResult => {
  const dispatch = useAppDispatch();
  const projectId = useAppSelector(selectProjectId);
  const { formatMessage } = useIntl();
  const { user } = useAuth();

  const {
    data: annotationProjectsData,
    isLoading: isAnnotationProjectsLoading,
    isFetching: isAnnotationProjectsFetching,
  } = useGetProjectsQuery(undefined, { skip: !user.isUser });

  const {
    data: batchesData,
    isLoading: isBatchesLoading,
    isFetching: isBatchesFetching,
  } = useGetBatchesQuery({ projectId: projectId as string }, { skip: !projectId || !user.isUser });

  // Convert projects data to dropdown options format
  const annotationProjectOptions: DropdownListItem[] = useMemo(() => {
    if (!annotationProjectsData?.data || annotationProjectsData.data.length === 0) {
      return [];
    }
    return annotationProjectsData.data.map((annotationProject) => ({
      value: String(annotationProject.id),
      displayValue: annotationProject.label,
    }));
  }, [annotationProjectsData]);

  const batchOptions: DropdownListItem[] = useMemo(() => {
    if (!batchesData?.data || batchesData.data.length === 0) {
      return [];
    }
    return batchesData.data.map((batch) => ({
      value: String(batch.id),
      displayValue: batch.label,
    }));
  }, [batchesData]);

  const handleProjectChange: DropdownChangeHandler = (event) => {
    const value = event.target?.value;
    if (typeof value === 'string') {
      dispatch(setProjectId(value));
      dispatch(clearBatchId());
    }
  };

  const handleBatchChange: DropdownChangeHandler = (event) => {
    const value = event.target?.value;
    if (typeof value === 'string') {
      dispatch(setBatchId(value));
    }
  };
  const theme = useTheme();
  const navigate = useNavigate();

  const handleAction = (taskId: string) => {
    navigate(`${getAnnotationToolAnnotatorQueuePath()}/annotation/${taskId}`);
  };

  const columns: GridColDef<AnnotationTaskResponse>[] = useMemo(
    () => [
      // {
      //   field: 'allocationNumber',
      //   headerName: 'Allocation #',
      //   minWidth: 130,
      //   type: 'string',
      //   align: 'left',
      //   headerAlign: 'left'
      // },
      {
        field: 'encounterId',
        headerName: 'Encounter ID',
        minWidth: 216,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          const encounterId = params?.row.encounterId;
          const taskId = params?.row.id;
          return (
            <OverflowTooltip title={encounterId ?? ''}>
              <Link
                to={`${getAnnotationToolAnnotatorQueuePath()}/annotation/${taskId}`}
                style={{
                  textDecoration: 'none',
                  color: theme.palette.primary.main,
                  fontWeight: 'bold',
                }}
              >
                {encounterId}
              </Link>
            </OverflowTooltip>
          );
        },
      },
      {
        field: 'notesCount',
        headerName: '#Notes',
        minWidth: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'totalChars',
        headerName: 'Total characters',
        minWidth: 155,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'patientInfo',
        headerName: 'Patient ID/ Sex / Age',
        minWidth: 210,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value: any, row: AnnotationTaskResponse) => {
          if (!row) return '';
          const { patientId, patientSex, patientAge } = row;
          const parts = [
            patientId || '',
            patientSex || '',
            patientAge !== undefined && patientAge !== null ? String(patientAge) : '',
          ].filter(Boolean);
          return parts.join(' / ');
        },
      },
      {
        field: 'encounterType',
        headerName: 'Type',
        minWidth: 208,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value: any, row: AnnotationTaskResponse) => {
          return row?.encounterType ? row.encounterType.toLocaleLowerCase() : '';
        },
        renderCell: (params) => {
          const encounterType = params?.row.encounterType;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
              <Box
                sx={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '100%',
                  backgroundColor:
                    STATUS_BACKGROUND_MAPPING[getStatusCode(encounterType as StatusText)]
                      ?.backgroundColor,
                }}
              ></Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textTransform: 'capitalize' }}
              >
                {encounterType.toLocaleLowerCase()}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'encounterTimeInfo',
        headerName: 'Encounter start - Encounter end',
        minWidth: 322,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value: any, row: AnnotationTaskResponse) => {
          if (!row) return '';
          const { encounterStart, encounterEnd } = row;
          const parts = [
            dayjs(encounterStart).format('MM/DD/YYYY HH:mm') || '',
            dayjs(encounterEnd).format('MM/DD/YYYY HH:mm') || '',
          ].filter(Boolean);
          return parts.join(' - ');
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 210,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        sortComparator: (v1, v2) => {
          const priority: Record<string, number> = {
            [formatMessage({ id: 'workspaceCommon.content.table.status.pending' })]: 0,
            [formatMessage({ id: 'workspaceCommon.content.table.status.inprogress' })]: 1,
            [formatMessage({ id: 'workspaceCommon.content.table.status.draft' })]: 2,
            [formatMessage({ id: 'workspaceCommon.content.table.status.finalized' })]: 3,
            [formatMessage({ id: 'workspaceCommon.content.table.status.completed' })]: 4,
            [formatMessage({ id: 'workspaceCommon.content.table.status.active' })]: 5,
          };

          return (priority[v1] ?? 99) - (priority[v2] ?? 99);
        },
        valueGetter: (value: any, row: AnnotationTaskResponse) => {
          if (!row?.status) return '';
          return formatMessage({
            id: `workspaceCommon.content.table.status.${row.status.toLocaleLowerCase()}`,
          });
        },
        renderCell: (params) => {
          const status = params?.row.status;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
              <Box
                sx={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '100%',
                  backgroundColor:
                    STATUS_BACKGROUND_MAPPING[getStatusCode(status as StatusText)]?.backgroundColor,
                }}
              ></Box>
              <Typography variant="body2" color="text.secondary">
                {formatMessage({
                  id: `workspaceCommon.content.table.status.${status.toLocaleLowerCase()}`,
                })}
              </Typography>
            </Box>
          );
        },
      },
      // {
      //   field: 'savedCount',
      //   headerName: 'Saved',
      //   minWidth: 96,
      //   type: 'number',
      // },
      // {
      //   field: 'finalizedSummary',
      //   headerName: 'Finalized Stats',
      //   minWidth: 320,
      //   type: 'number',
      // },
      {
        field: 'completionDate',
        headerName: 'Completion date',
        minWidth: 209,
        type: 'date',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value?: string | null) => {
          return value ? new Date(value) : null;
        },
      },
      // {
      //   field: 'action',
      //   headerName: 'Action',
      //   minWidth: 150,
      //   type: 'string',
      //   align: 'left',
      //   headerAlign: 'left',
      //   renderCell: (params) => {
      //     const taskId = params?.row.id;
      //     return (
      //       <Button
      //       id={`assignment-task-action-open-${taskId}`}
      //       displayLabel="Open"
      //       variant="outlined"
      //       color="primary"
      //       size="medium"
      //       sx={{textTransform: 'none', fontSize: 14}}
      //       onClickHandler={() => handleAction(taskId)}/>
      //     );
      //   },
      // },
    ],
    [formatMessage, theme]
  );

  return {
    annotationProjectOptions,
    isAnnotationProjectsLoading: isAnnotationProjectsLoading || isAnnotationProjectsFetching,
    batchOptions,
    isBatchesLoading: isBatchesLoading || isBatchesFetching,
    handleProjectChange,
    handleBatchChange,
    columns,
  };
};
